import { api, AppConfig } from '../lib/api';
import { useQuery } from './useQuery';

// Tiny wrapper so multiple components can share the same /config response
// without hitting the endpoint repeatedly — React re-renders are cheap but
// the HTTP round-trip isn't free. The response is tiny and stable for the
// life of the app session.

let cached: AppConfig | null = null;

export function useConfig(): { config: AppConfig | null; isLoading: boolean } {
  const { data, isLoading } = useQuery(
    () => (cached ? Promise.resolve(cached) : api.config().then((c) => { cached = c; return c; })),
  );
  return { config: data, isLoading };
}

export function jiraIssueUrl(config: AppConfig | null, issueKey: string): string | null {
  if (!config?.jira_browse_url) return null;
  return `${config.jira_browse_url}/${issueKey}`;
}

/**
 * Link to a specific worklog on its Jira issue.
 *
 * `issueRef` may be the readable key or Jira's numeric issue id — /browse
 * resolves both. `jiraWorklogId` must be JIRA's worklog id, not Tempo's:
 * Tempo mirrors its worklogs into Jira under different ids, and passing a
 * Tempo id here silently lands on the issue with nothing highlighted.
 *
 * Falls back to a plain issue link when the worklog id is unknown — which is
 * the normal case for worklogs snapshotted before the sync started capturing
 * it, and permanent for worklogs Tempo has since deleted.
 */
export function jiraWorklogUrl(
  config: AppConfig | null,
  issueRef: string | null,
  jiraWorklogId: string | null,
): string | null {
  if (!config?.jira_browse_url || !issueRef) return null;
  const base = `${config.jira_browse_url}/${encodeURIComponent(issueRef)}`;
  if (!jiraWorklogId) return base;
  const tab = encodeURIComponent('com.atlassian.jira.plugin.system.issuetabpanels:worklog-tabpanel');
  return `${base}?focusedWorklogId=${encodeURIComponent(jiraWorklogId)}&page=${tab}`;
}

export function chargeoverInvoiceUrl(config: AppConfig | null, invoiceId: string | number | null | undefined): string | null {
  if (!config?.chargeover_invoice_url_template || invoiceId == null) return null;
  return config.chargeover_invoice_url_template.replace('{id}', String(invoiceId));
}

export function hubspotDealUrl(config: AppConfig | null, dealId: string | null | undefined): string | null {
  if (!config?.hubspot_deal_url_template || dealId == null) return null;
  return config.hubspot_deal_url_template.replace('{id}', String(dealId));
}
